// FinanceManagement.js
import React, { useState, useEffect } from "react";
import {
  FaMoneyBillWave,
  FaChartLine,
  FaCalendarAlt,
  FaFilter,
  FaDownload,
  FaSpinner,
  FaExclamationTriangle,
  FaUser,
  FaGamepad
} from "react-icons/fa";
import axios from "axios";
import { jsPDF } from "jspdf";
import 'jspdf-autotable';
import { useArenaAuth } from "../../context/ArenaAuthContext";

const FinanceManagement = () => {
  const [facilities, setFacilities] = useState([]);
  const [selectedFacility, setSelectedFacility] = useState("all");
  const [dateRange, setDateRange] = useState({ 
    start: new Date(new Date().getFullYear(), 0, 1).toISOString().split('T')[0], // Start of current year
    end: new Date(new Date().getFullYear(), 11, 31).toISOString().split('T')[0] // End of current year
  });
  const [financialData, setFinancialData] = useState(null);
  const [bookingDetails, setBookingDetails] = useState([]);
  const [loading, setLoading] = useState(true);
  const { arenaUser } = useArenaAuth();

  useEffect(() => {
    if (arenaUser?.arenaId) {
      fetchFacilities();
      fetchFinancialData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [arenaUser?.arenaId]);

  useEffect(() => {
    if (dateRange.start && dateRange.end) {
      fetchFinancialData();
    }
  }, [selectedFacility, dateRange]);

  const fetchFacilities = async () => {
    try {
      const arenaId = arenaUser?.arenaId;
      const response = await axios.get(
        `${process.env.REACT_APP_BASE_URL}/api/arena-dashboard/sports-facility-info/${arenaId}`
      );
      
      const facilitiesWithAll = [
        { id: "all", name: "All Facilities" },
        ...response.data.map(facility => ({
          id: facility.id,
          name: facility.sportNames
        }))
      ];
      
      setFacilities(facilitiesWithAll);
    } catch (error) {
      console.error("Error fetching facilities:", error);
    }
  };

  const fetchFinancialData = async () => {
    try {
      setLoading(true);
      const arenaId = arenaUser?.arenaId;
      
      const params = {
        arenaId,
        facilityId: selectedFacility === "all" ? null : selectedFacility,
        startDate: dateRange.start,
        endDate: dateRange.end
      };
      
      const response = await axios.get(
        `${process.env.REACT_APP_BASE_URL}/api/arena-dashboard/financial-data`, 
        { params }
      );
      
      setFinancialData(response.data.summary);
      setBookingDetails(response.data.bookings);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching financial data:", error);
      setLoading(false);
    }
  };

  const handleFacilityChange = (e) => {
    setSelectedFacility(e.target.value);
  };

  const handleDateChange = (e) => {
    setDateRange({ ...dateRange, [e.target.name]: e.target.value });
  };

  const handleGenerateReport = () => {
    if (!financialData || !bookingDetails) {
      alert("No financial data available to generate report");
      return;
    }

    try {
      const doc = new jsPDF();
      const pageWidth = doc.internal.pageSize.width;
      
      // Header
      doc.setFontSize(20);
      doc.text("Financial Report", pageWidth / 2, 20, { align: "center" });
      
      // Arena and date info
      doc.setFontSize(12);
      doc.text(`Arena: ${arenaUser?.name || "Arena"}`, 20, 35);
      doc.text(`Facility: ${facilities.find(f => f.id === selectedFacility)?.name || "All"}`, 20, 45);
      doc.text(`Period: ${dateRange.start} to ${dateRange.end}`, 20, 55);
      doc.text(`Generated: ${new Date().toLocaleDateString()}`, 20, 65);
      
      // Financial Summary
      doc.setFontSize(16);
      doc.text("Financial Summary", 20, 85);
      
      doc.setFontSize(12);
      doc.text(`Total Revenue: $${financialData.totalRevenue.toLocaleString()}`, 20, 100);
      doc.text(`Total Bookings: ${financialData.totalBookings}`, 20, 110);
      doc.text(`Average Revenue per Booking: $${financialData.averageRevenue.toFixed(2)}`, 20, 120);
      doc.text(`Monthly Revenue: $${financialData.monthlyRevenue.toLocaleString()}`, 20, 130);
      
      // Booking Details Table
      if (bookingDetails.length > 0) {
        doc.setFontSize(16);
        doc.text("Booking Details", 20, 150);
        
        const tableData = bookingDetails.map(booking => [
          booking.user_name,
          booking.sport_name,
          booking.booking_date,
          booking.time_slot,
          `$${booking.amount}`
        ]);
        
        // Check if autoTable is available
        if (typeof doc.autoTable === 'function') {
          doc.autoTable({
            head: [['User', 'Sport', 'Date', 'Time', 'Amount']],
            body: tableData,
            startY: 160,
            styles: { fontSize: 10 },
            headStyles: { fillColor: [63, 81, 181] },
          });
        } else {
          // Fallback: manual table creation
          let yPosition = 170;
          doc.setFontSize(10);
          
          // Table headers
          doc.text("User", 20, yPosition);
          doc.text("Sport", 60, yPosition);
          doc.text("Date", 100, yPosition);
          doc.text("Time", 140, yPosition);
          doc.text("Amount", 170, yPosition);
          
          yPosition += 10;
          
          // Table data
          tableData.forEach((row, index) => {
            if (yPosition > 280) { // Start new page if needed
              doc.addPage();
              yPosition = 20;
            }
            
            doc.text(row[0], 20, yPosition);
            doc.text(row[1], 60, yPosition);
            doc.text(row[2], 100, yPosition);
            doc.text(row[3], 140, yPosition);
            doc.text(row[4], 170, yPosition);
            
            yPosition += 8;
          });
        }
      } else {
        doc.setFontSize(12);
        doc.text("No booking details available for the selected period.", 20, 150);
      }
      
      // Save the PDF
      const facilityName = facilities.find(f => f.id === selectedFacility)?.name || "All";
      const fileName = `Financial_Report_${facilityName}_${dateRange.start}_to_${dateRange.end}.pdf`;
      doc.save(fileName);
      
    } catch (error) {
      console.error("Error generating PDF:", error);
      alert("Error generating PDF report. Please try again.");
    }
  };

  return (
    <div className="p-3 sm:p-4 md:p-6 lg:p-8">
      <div className="mb-4 sm:mb-6 md:mb-8">
        <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900 flex items-center gap-2 sm:gap-3 mb-1 sm:mb-2">
          <FaMoneyBillWave className="text-[#0071bc] text-lg sm:text-xl md:text-2xl" />
          <span className="truncate">Finance Management</span>
        </h2>
        <p className="text-gray-600 text-xs sm:text-sm md:text-base">Track revenue, bookings, and generate financial reports</p>
      </div>

      <div className="bg-white p-3 sm:p-4 md:p-6 rounded-xl sm:rounded-2xl shadow-lg border border-gray-200 mb-4 sm:mb-6 md:mb-8">
        <div className="flex flex-col gap-4 sm:gap-6">
          <div className="flex-1">
            <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-1 sm:mb-2 flex items-center gap-1 sm:gap-2">
              <FaFilter className="text-[#0071bc] text-xs sm:text-sm" />
              Select Facility
            </label>
            <select
              value={selectedFacility}
              onChange={handleFacilityChange}
              className="w-full px-3 sm:px-4 py-2 sm:py-3 text-sm sm:text-base border border-gray-300 rounded-lg sm:rounded-xl focus:ring-2 focus:ring-[#0071bc] focus:border-transparent transition-all duration-300 bg-white"
            >
              {facilities.map((facility) => (
                <option key={facility.id} value={facility.id}>
                  {facility.name}
                </option>
              ))}
            </select>
          </div>

          <div className="flex-1">
            <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-1 sm:mb-2 flex items-center gap-1 sm:gap-2">
              <FaCalendarAlt className="text-green-500 text-xs sm:text-sm" />
              Date Range
            </label>
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-3">
              <input
                type="date"
                name="start"
                value={dateRange.start}
                onChange={handleDateChange}
                className="flex-1 px-3 sm:px-4 py-2 sm:py-3 text-sm sm:text-base border border-gray-300 rounded-lg sm:rounded-xl focus:ring-2 focus:ring-[#0071bc] focus:border-transparent transition-all duration-300"
              />
              <span className="text-gray-500 font-medium text-center text-sm">to</span>
              <input
                type="date"
                name="end"
                value={dateRange.end}
                onChange={handleDateChange}
                className="flex-1 px-3 sm:px-4 py-2 sm:py-3 text-sm sm:text-base border border-gray-300 rounded-lg sm:rounded-xl focus:ring-2 focus:ring-[#0071bc] focus:border-transparent transition-all duration-300"
              />
            </div>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-8 sm:py-12 md:py-16">
          <div className="inline-flex items-center gap-2 sm:gap-3 bg-white p-4 sm:p-6 md:p-8 rounded-xl sm:rounded-2xl shadow-lg">
            <FaSpinner className="animate-spin text-[#0071bc] text-lg sm:text-xl md:text-2xl" />
            <p className="text-gray-600 font-medium text-sm sm:text-base">Loading financial data...</p>
          </div>
        </div>
      ) : financialData ? (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-4 md:gap-6 mb-4 sm:mb-6 md:mb-8">
            <div className="bg-white overflow-hidden shadow-lg sm:shadow-xl rounded-xl sm:rounded-2xl border border-gray-100 hover:shadow-2xl transition-all duration-300">
              <div className="p-3 sm:p-4 md:p-6">
                <div className="flex items-center gap-2 sm:gap-3">
                  <div className="flex-shrink-0">
                    <div className="w-8 h-8 sm:w-10 sm:h-10 md:w-12 md:h-12 bg-green-100 rounded-lg sm:rounded-xl flex items-center justify-center">
                      <FaMoneyBillWave className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 text-green-600" />
                    </div>
                  </div>
                  <div className="min-w-0 flex-1">
                    <dl>
                      <dt className="text-[10px] sm:text-xs md:text-sm font-medium text-gray-500 truncate">Total Revenue</dt>
                      <dd className="text-sm sm:text-lg md:text-2xl font-bold text-gray-900">${financialData.totalRevenue.toLocaleString()}</dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="bg-white overflow-hidden shadow-lg sm:shadow-xl rounded-xl sm:rounded-2xl border border-gray-100 hover:shadow-2xl transition-all duration-300">
              <div className="p-3 sm:p-4 md:p-6">
                <div className="flex items-center gap-2 sm:gap-3">
                  <div className="flex-shrink-0">
                    <div className="w-8 h-8 sm:w-10 sm:h-10 md:w-12 md:h-12 bg-blue-100 rounded-lg sm:rounded-xl flex items-center justify-center">
                      <FaCalendarAlt className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 text-blue-600" />
                    </div>
                  </div>
                  <div className="min-w-0 flex-1">
                    <dl>
                      <dt className="text-[10px] sm:text-xs md:text-sm font-medium text-gray-500 truncate">Total Bookings</dt>
                      <dd className="text-sm sm:text-lg md:text-2xl font-bold text-gray-900">{financialData.totalBookings}</dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="bg-white overflow-hidden shadow-lg sm:shadow-xl rounded-xl sm:rounded-2xl border border-gray-100 hover:shadow-2xl transition-all duration-300">
              <div className="p-3 sm:p-4 md:p-6">
                <div className="flex items-center gap-2 sm:gap-3">
                  <div className="flex-shrink-0">
                    <div className="w-8 h-8 sm:w-10 sm:h-10 md:w-12 md:h-12 bg-purple-100 rounded-lg sm:rounded-xl flex items-center justify-center">
                      <FaChartLine className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 text-purple-600" />
                    </div>
                  </div>
                  <div className="min-w-0 flex-1">
                    <dl>
                      <dt className="text-[10px] sm:text-xs md:text-sm font-medium text-gray-500 truncate">Avg/Booking</dt>
                      <dd className="text-sm sm:text-lg md:text-2xl font-bold text-gray-900">${financialData.averageRevenue.toFixed(2)}</dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="bg-white overflow-hidden shadow-lg sm:shadow-xl rounded-xl sm:rounded-2xl border border-gray-100 hover:shadow-2xl transition-all duration-300">
              <div className="p-3 sm:p-4 md:p-6">
                <div className="flex items-center gap-2 sm:gap-3">
                  <div className="flex-shrink-0">
                    <div className="w-8 h-8 sm:w-10 sm:h-10 md:w-12 md:h-12 bg-orange-100 rounded-lg sm:rounded-xl flex items-center justify-center">
                      <FaMoneyBillWave className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 text-orange-600" />
                    </div>
                  </div>
                  <div className="min-w-0 flex-1">
                    <dl>
                      <dt className="text-[10px] sm:text-xs md:text-sm font-medium text-gray-500 truncate">This Month</dt>
                      <dd className="text-sm sm:text-lg md:text-2xl font-bold text-gray-900">${financialData.monthlyRevenue?.toLocaleString() || '0'}</dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Booking Details Table */}
          {bookingDetails.length > 0 && (
            <div className="bg-white rounded-xl sm:rounded-2xl shadow-lg border border-gray-200 overflow-hidden mb-4 sm:mb-6 md:mb-8">
              <div className="p-3 sm:p-4 md:p-6 border-b border-gray-200">
                <h3 className="text-base sm:text-lg md:text-xl font-bold text-gray-900">Recent Bookings</h3>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-3 sm:px-4 md:px-6 py-2 sm:py-3 text-left text-[10px] sm:text-xs font-medium text-gray-500 uppercase tracking-wider">
                        <div className="flex items-center gap-1 sm:gap-2">
                          <FaUser className="text-blue-500 text-[10px] sm:text-xs" />
                          User
                        </div>
                      </th>
                      <th className="px-3 sm:px-4 md:px-6 py-2 sm:py-3 text-left text-[10px] sm:text-xs font-medium text-gray-500 uppercase tracking-wider">
                        <div className="flex items-center gap-1 sm:gap-2">
                          <FaGamepad className="text-green-500 text-[10px] sm:text-xs" />
                          Sport
                        </div>
                      </th>
                      <th className="px-3 sm:px-4 md:px-6 py-2 sm:py-3 text-left text-[10px] sm:text-xs font-medium text-gray-500 uppercase tracking-wider hidden sm:table-cell">
                        <div className="flex items-center gap-1 sm:gap-2">
                          <FaCalendarAlt className="text-purple-500 text-[10px] sm:text-xs" />
                          Date
                        </div>
                      </th>
                      <th className="px-3 sm:px-4 md:px-6 py-2 sm:py-3 text-left text-[10px] sm:text-xs font-medium text-gray-500 uppercase tracking-wider hidden md:table-cell">
                        Time
                      </th>
                      <th className="px-3 sm:px-4 md:px-6 py-2 sm:py-3 text-left text-[10px] sm:text-xs font-medium text-gray-500 uppercase tracking-wider">
                        <div className="flex items-center gap-1 sm:gap-2">
                          <FaMoneyBillWave className="text-green-500 text-[10px] sm:text-xs" />
                          Amount
                        </div>
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {bookingDetails.slice(0, 10).map((booking, index) => (
                      <tr key={index} className="hover:bg-gray-50 transition-colors duration-200">
                        <td className="px-3 sm:px-4 md:px-6 py-2 sm:py-3 md:py-4 whitespace-nowrap text-[10px] sm:text-xs md:text-sm font-medium text-gray-900">
                          {booking.user_name}
                        </td>
                        <td className="px-3 sm:px-4 md:px-6 py-2 sm:py-3 md:py-4 whitespace-nowrap text-[10px] sm:text-xs md:text-sm text-gray-700">
                          {booking.sport_name}
                        </td>
                        <td className="px-3 sm:px-4 md:px-6 py-2 sm:py-3 md:py-4 whitespace-nowrap text-[10px] sm:text-xs md:text-sm text-gray-700 hidden sm:table-cell">
                          {booking.booking_date}
                        </td>
                        <td className="px-3 sm:px-4 md:px-6 py-2 sm:py-3 md:py-4 whitespace-nowrap text-[10px] sm:text-xs md:text-sm text-gray-700 hidden md:table-cell">
                          {booking.time_slot}
                        </td>
                        <td className="px-3 sm:px-4 md:px-6 py-2 sm:py-3 md:py-4 whitespace-nowrap text-[10px] sm:text-xs md:text-sm font-semibold text-green-600">
                          ${booking.amount}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {bookingDetails.length > 10 && (
                <div className="px-3 sm:px-4 md:px-6 py-2 sm:py-3 md:py-4 bg-blue-50 border-t border-gray-200">
                  <p className="text-[10px] sm:text-xs md:text-sm text-gray-600">
                    Showing 10 of {bookingDetails.length} bookings. Download report for complete details.
                  </p>
                </div>
              )}
            </div>
          )}
        </>
      ) : (
        <div className="text-center py-8 sm:py-12 md:py-16 bg-gradient-to-r from-gray-50 to-blue-50 rounded-xl sm:rounded-2xl border-2 border-dashed border-gray-300">
          <FaExclamationTriangle className="text-4xl sm:text-5xl md:text-6xl text-gray-400 mx-auto mb-2 sm:mb-4" />
          <h3 className="text-base sm:text-lg md:text-xl font-semibold text-gray-700 mb-1 sm:mb-2">No Financial Data Available</h3>
          <p className="text-gray-600 text-xs sm:text-sm md:text-base px-4">No financial data available for the selected period and facility.</p>
        </div>
      )}

      <div className="flex justify-center mt-4 sm:mt-6 md:mt-8">
        <button 
          onClick={handleGenerateReport} 
          className="bg-[#0071bc] text-white px-4 sm:px-6 md:px-8 py-2 sm:py-3 md:py-4 rounded-lg sm:rounded-xl font-semibold text-xs sm:text-sm md:text-base hover:bg-[#005a94] transition-all duration-300 flex items-center gap-2 sm:gap-3 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
          disabled={loading || !financialData}
        >
          <FaDownload className="text-sm sm:text-base md:text-lg" /> 
          Generate PDF Report
        </button>
      </div>
    </div>
  );
};

export default FinanceManagement;
